import { useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { CreditCard, Heart, Loader2 } from "lucide-react";
import { postApi, useApi } from "@/lib/api";

const AmountPicker = ({ presets, amount, setAmount }) => (
  <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${presets.length}, minmax(0, 1fr))` }}>
    {presets.map((p) => (
      <button key={p} type="button" onClick={() => setAmount(String(p))} data-testid={`donate-preset-${p}`}
        className={`h-14 font-display text-xl border-2 transition-colors duration-200 ${String(p) === amount ? "bg-primary border-primary text-white" : "border-white/20 text-white hover:border-white"}`}>
        {p} €
      </button>
    ))}
  </div>
);

const CardButton = ({ amount, name, message, disabled, monthly }) => {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const res = await postApi("/payments/checkout", { amount, name, message, monthly, origin_url: window.location.origin });
      if (!res?.checkout_url) throw new Error();
      window.location.href = res.checkout_url;
    } catch {
      toast.error("Le paiement par carte n'a pas pu démarrer. Réessaie.");
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={pay} disabled={disabled || busy} data-testid="donate-card-btn"
      className="w-full h-14 inline-flex items-center justify-center gap-3 bg-white text-black font-display uppercase text-xl hover:bg-primary hover:text-white transition-colors duration-200 disabled:opacity-40">
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} {monthly ? `Soutenir ${amount} € / mois` : "Donner par carte"}
    </button>
  );
};

const Frequency = ({ monthly, setMonthly }) => (
  <div className="grid grid-cols-2 border-2 border-white/20" role="group" aria-label="Fréquence du don">
    {[[false, "Une fois"], [true, "Chaque mois"]].map(([m, label]) => (
      <button key={label} type="button" onClick={() => setMonthly(m)} aria-pressed={monthly === m} data-testid={`donate-frequency-${m ? "monthly" : "once"}`}
        className={`h-12 uppercase text-sm font-bold tracking-[0.15em] transition-colors duration-200 ${monthly === m ? "bg-white text-black" : "text-white hover:bg-white/10"}`}>
        {label}
      </button>
    ))}
  </div>
);

export const DonateForm = () => {
  const { data: cfg, isLoading } = useApi("/donations/config");
  const [amount, setAmount] = useState("10");
  const [monthly, setMonthly] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(null);

  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  if (!cfg?.enabled && !cfg?.stripe)
    return <p className="text-zinc-400 border border-white/10 p-6" data-testid="donate-disabled">Les dons en ligne arrivent très bientôt.</p>;
  if (done)
    return (
      <div className="border-2 border-primary p-8" data-testid="donate-success">
        <Heart className="w-8 h-8 text-primary" />
        <p className="font-display uppercase text-3xl text-white mt-4">Merci{name ? ` ${name}` : ""} !</p>
        <p className="text-zinc-300 mt-2">Ton soutien de {done.amount.toFixed(2)} € fait tourner la radio. Un e-mail de remerciement t'a été envoyé.</p>
        <button onClick={() => setDone(null)} data-testid="donate-again-btn" className="mt-6 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-white">Faire un autre don</button>
      </div>
    );

  const value = Number(amount);
  const valid = monthly ? (cfg.monthly || []).includes(value) : value >= cfg.min && value <= cfg.max;
  const switchFrequency = (m) => { setMonthly(m); setAmount(m ? "5" : "10"); };

  return (
    <div className="space-y-5" data-testid="donate-form">
      {cfg.stripe && <Frequency monthly={monthly} setMonthly={switchFrequency} />}
      <AmountPicker presets={monthly ? cfg.monthly : cfg.presets} amount={amount} setAmount={setAmount} />
      {monthly && <p className="text-sm text-zinc-300" data-testid="donate-monthly-note">Prélevé chaque mois par carte. Tu peux arrêter à tout moment avec le lien « Gérer mon soutien » reçu par e-mail.</p>}
      {!monthly && <div className="flex items-center border-2 border-white/20 focus-within:border-white">
        <input type="number" min={cfg.min} max={cfg.max} step="1" value={amount} onChange={(e) => setAmount(e.target.value)}
          data-testid="donate-amount-input" aria-label="Montant libre"
          className="flex-1 bg-transparent px-5 h-14 text-white text-lg outline-none [color-scheme:dark]" />
        <span className="px-5 text-zinc-400 font-bold">EUR</span>
      </div>}
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Ton nom ou pseudo (optionnel)"
        data-testid="donate-name-input" className="w-full bg-transparent border border-white/20 px-5 h-12 text-white outline-none focus:border-white" />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={3} placeholder="Un mot pour l'équipe (optionnel)"
        data-testid="donate-message-input" className="w-full bg-transparent border border-white/20 px-5 py-3 text-white outline-none focus:border-white" />
      {!valid && <p className="text-sm text-red-400" data-testid="donate-amount-error">Montant entre {cfg.min} € et {cfg.max} €.</p>}
      {cfg.stripe && cfg.stripe_mode !== "live" && <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400" data-testid="donate-stripe-test-note">Paiement par carte en mode test — carte 4242 4242 4242 4242, aucun vrai paiement</p>}
      {cfg.stripe && <CardButton amount={value} name={name} message={message} disabled={!valid} monthly={monthly} />}
      {cfg.enabled && !monthly && cfg.mode !== "live" && <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500" data-testid="donate-sandbox-note">Mode test PayPal — aucun vrai paiement</p>}
      {cfg.enabled && !monthly && <div className={valid ? "" : "opacity-40 pointer-events-none"} data-testid="donate-paypal-buttons">
        <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: cfg.currency, intent: "capture" }}>
          <PayPalButtons
            style={{ layout: "vertical", color: "gold", shape: "rect", label: "donate" }}
            forceReRender={[value, name, message]}
            createOrder={async () => (await postApi("/donations/orders", { amount: value, name, message })).id}
            onApprove={async (data) => {
              const res = await postApi(`/donations/orders/${data.orderID}/capture`, {});
              setDone(res);
              toast.success("Merci pour ton soutien !");
            }}
            onError={() => toast.error("Le paiement n'a pas abouti. Réessaie.")}
          />
        </PayPalScriptProvider>
      </div>}
    </div>
  );
};
